import React, { FC, memo, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";

import { AppDispatch } from "../../redux/reducer/store";
import {
  reducerDarkTheme,
  reducerTheme,
} from "../../redux/reducer/theme/reducers/reducerTheme";
import { convertHexToRGBA } from "../../utils/formatColor";

import Select from "../custom/select/select";

interface IThemeSwitcher {
  isOpen?: boolean,
}

const themeOptions = [
  { value: "purple", label: "Фиолетовый" },
  { value: "lightBlue", label: "Светло голубой" },
  { value: "blue", label: "Голубой" },
  { value: "darkBlue", label: "Синий" },
  { value: "light", label: "Светло серый" },
  { value: "dark", label: "Серый" },
  { value: "black", label: "Черный" },
  { value: "yellow", label: "Желтый" },
  { value: "red", label: "Красный" },
  { value: "orange", label: "Оранжевый" },
  { value: "green", label: "Зеленый" },
];

const ThemeSwitcher: FC<IThemeSwitcher> = memo(({isOpen = true}) => {
  const dispatch = useDispatch<AppDispatch>();
  const sliderRef = useRef<HTMLDivElement | null>(null);

  const theme = useSelector((state: any) => state.theme.theme);
  const { darkTheme } = useSelector((state: any) => state.theme.theme);

  const [selectedColor, setSelectedColor] = useState<string>("#ffffff");
  const [isSwitchValue, setIsSwitchValue] = useState<boolean>(
    !darkTheme.isDarkTheme
  );
  const [isOpenRange, setIsOpenRange] = useState<boolean>(false);
  const [saturation, setSaturation] = useState<number>(() => {
    const savedSaturation = localStorage.getItem("saturation");
    return savedSaturation ? Number(savedSaturation) : 0;
  });

  useEffect(() => {
    localStorage.setItem("saturation", saturation.toString());
  }, [saturation]);

  useEffect(() => {
    if (darkTheme.isDarkTheme) {
      setDarkTheme();
    }
    // eslint-disable-next-line
  }, [darkTheme]);

  useEffect(() => {
    const customThemeOptions = JSON.parse(
      localStorage.getItem("customThemeOptions") || "null"
    );
    if (theme.theme === "custom" && customThemeOptions) {
      applyStyles(customThemeOptions);
    }
    document.documentElement.dataset.theme = theme.theme;
    localStorage.setItem("theme", theme.theme);
  }, [theme]);

  useEffect(() => {
    if (isSwitchValue) {
      document.documentElement.style.setProperty(
        "--text-color",
        "rgb(39, 40, 53)"
      );
      document.documentElement.style.setProperty(
        "--body-color",
        "rgb(255, 255, 255)"
      );
      document.documentElement.style.setProperty(
        "--background-input-color",
        "rgb(255,255,255)"
      );
      document.documentElement.style.removeProperty('--table-head-bg-color');
      document.documentElement.style.removeProperty('--main-text-color');
      document.documentElement.style.removeProperty('--btns-bg-color');
      document.documentElement.style.removeProperty('--main-color');
    
      dispatch(
        reducerDarkTheme({
          ...darkTheme,
          isDarkTheme: false,
        })
      );
      localStorage.setItem(
        "darkTheme",
        JSON.stringify({
          color: darkTheme.color,
          backgroundColor: darkTheme.backgroundColor,
          isDarkTheme: false,
        })
      );
    } else {
      if (!localStorage.getItem("saturation")) {
        setSaturation(0);
      }
      dispatch(
        reducerDarkTheme({
          ...darkTheme,
          isDarkTheme: true,
        })
      );
      localStorage.setItem(
        "darkTheme",
        JSON.stringify({
          color: darkTheme.color,
          backgroundColor: darkTheme.backgroundColor,
          isDarkTheme: true,
        })
      );
    }
    // eslint-disable-next-line
  }, [isSwitchValue]);

  const setDarkTheme = () => {
    document.documentElement.style.setProperty(
      "--text-color",
      "rgb(255, 255, 255)"
    );
    document.documentElement.style.setProperty(
      "--table-head-bg-color",
      "rgb(91, 87, 112)"
    );
    document.documentElement.style.setProperty(
      "--main-text-color",
      "rgb(177, 161, 222)"
    );
    document.documentElement.style.setProperty(
      "--main-color",
      "rgb(78, 62, 124)"
    );
    document.documentElement.style.setProperty(
      "--btns-bg-color",
      "rgba(134, 128, 151, 0.5)"
    );

    document.documentElement.style.setProperty(
      "--body-color",
      darkTheme.backgroundColor
    );
    document.documentElement.style.setProperty(
      "--background-input-color",
      darkTheme.backgroundColor.includes("rgba")
        ? "rgba(74, 74, 74, 1)"
        : darkTheme.backgroundColor
    );
  };

  const handleChangeTheme = (event: { value: string; label: string }) => {
    const customOptions = JSON.parse(
      localStorage.getItem("customThemeOptions") || "[]"
    );
    applyStyles(customOptions, "delete");
    localStorage.removeItem("customThemeOptions");
    dispatch(reducerTheme(event.value));
    setSelectedColor("#ffffff");
  };

  const handleChangeColorPicker = (e: React.ChangeEvent<HTMLInputElement>) => {
    const color = e.target.value;
    const customOptions = [
      { property: "--table-border-color", value: convertHexToRGBA(color, 0.2) },
      { property: "--btns-bg-color", value: convertHexToRGBA(color, 0.4) },
      { property: "--avatar-color", value: convertHexToRGBA(color, 0.24) },
      { property: "--active-tab-color", value: "#ffffff" },
      { property: "--main-text-color", value: color },
      { property: "--main-color", value: color },
      {
        property: "--table-head-bg-color",
        value: convertHexToRGBA(color, 0.5),
      },
      {
        property: "--menu-item-border-color",
        value: convertHexToRGBA(color, 0.32),
      },
      { property: "--background-color", value: convertHexToRGBA(color, 0.05) },
      {
        property: "--hover-edit-cell-color",
        value: convertHexToRGBA(color, 0.1),
      },
    ];

    localStorage.setItem("customThemeOptions", JSON.stringify(customOptions));
    dispatch(reducerTheme("custom"));
    applyStyles(customOptions);
    setSelectedColor(color);
  };

  const handleChangeRangeInput = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newSaturation = Number(event.target.value);
    setSaturation(newSaturation);

    const lightColor = { r: 80, g: 80, b: 80 };
    const darkColor = { r: 24, g: 28, b: 30 };

    const r = Math.round(
      lightColor.r + (darkColor.r - lightColor.r) * (newSaturation / 100)
    );
    const g = Math.round(
      lightColor.g + (darkColor.g - lightColor.g) * (newSaturation / 100)
    );
    const b = Math.round(
      lightColor.b + (darkColor.b - lightColor.b) * (newSaturation / 100)
    );

    const backgroundColorStr = `rgb(${r}, ${g}, ${b})`;
    const textColorStr =
      newSaturation === 0 ? "rgb(39, 40, 53)" : "rgb(236, 235, 235)";

    const tableHeadBackground =
      newSaturation === 0 ? "rgb(240, 238, 251)" : "rgb(91, 87, 112)";

    document.documentElement.style.setProperty("--text-color", textColorStr);
    document.documentElement.style.setProperty(
      "--body-color",
      backgroundColorStr
    );
    document.documentElement.style.setProperty(
      "--background-input-color",
      textColorStr
    );
    document.documentElement.style.setProperty(
      "--table-head-bg-color",
      tableHeadBackground
    );

    const themeData = {
      color: textColorStr,
      backgroundColor: backgroundColorStr,
      isDarkTheme: newSaturation !== 0,
    };

    dispatch(reducerDarkTheme(themeData));
    localStorage.setItem("darkTheme", JSON.stringify(themeData));
    setIsSwitchValue(newSaturation === 0);
  };

  const applyStyles = (
    styleOptions: { property: string; value: string }[],
    purpose?: "delete"
  ) => {
    if (purpose === "delete") {
      const customThemeOptions = JSON.parse(
        localStorage.getItem("customThemeOptions") || "[]"
      );
      customThemeOptions.forEach((elem: any) => {
        document.documentElement.style.removeProperty(elem.property);
      });
    } else {
      styleOptions.forEach((elem) => {
        document.documentElement.style.setProperty(elem.property, elem.value);
      });
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (sliderRef.current && !sliderRef.current.contains(e.target as Node)) {
        setIsOpenRange(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const themeSelectValue = themeOptions.filter(
    (obj) => obj.value === theme.theme
  );

  return isOpen ? (
    <div className="theme__switch__container">
      <div className="theme__select__container">
        <Select
          name="theme"
          placeholder="Выберите тему..."
          className="theme__select"
          options={themeOptions}
          value={themeSelectValue}
          defaultValue={themeSelectValue}
          propsChange={handleChangeTheme}
        />
      </div>

      <label className="color__selector">
        {" "}
        <span className="circle" style={{ background: selectedColor }} />
        <input
          type="color"
          className="color__picker"
          defaultValue={selectedColor}
          onChange={handleChangeColorPicker}
        />{" "}
      </label>

      <div className="toggle-switch-container">
        <div
          className={clsx("toggle-option", "toggle-option_day", {
            "toggle-option_active": isSwitchValue,
          })}
          onClick={() => {
            setIsOpenRange(false);
            setIsSwitchValue(true);
          }}
        />
        <div
          className={clsx("toggle-option", "toggle-option_night", {
            "toggle-option_active": !isSwitchValue,
          })}
          onClick={() => {
            setIsOpenRange(true);
            setIsSwitchValue(false);
            setDarkTheme();
          }}
        />
        {isOpenRange && (
          <div className="slider-container" ref={sliderRef}>
            <label htmlFor="saturation-slider" className="slider-label">
              {" "}
              Насыщенность черного{" "}
            </label>{" "}
            <input
              id="saturation-slider"
              type="range"
              min="0"
              max="100"
              className="slider"
              value={saturation}
              onChange={handleChangeRangeInput}
              style={{ transition: "none" }}
            />
          </div>
        )}
      </div>
    </div>
  ) : (<></>);
});

export default ThemeSwitcher;
